import React from 'react'
import * as Sentry from '@sentry/browser'
import { Flex } from 'components/Box'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { SidebarList } from '../SidebarList'
import { Sidebar, Content } from './Layout.styles'
import GroupsError from './GroupsError'
import { useGetComponentGroupsQuery } from 'gqlTypes'
import { useGetIdentifiers } from '../../operations/useGetIdentifiers'
// really needed here for the groups call
// eslint-disable-next-line
import { ComponentFilterType } from 'gqlTypes/expansiveTypes'

/**
 * TYPES
 */
interface LayoutProps {
  onSelect: (path: string) => void
  selected: string
  type: string
}

/**
 * COMPONENT
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  onSelect,
  selected,
  type,
}) => {
  const { shareIdentifier, versionShortId } = useGetIdentifiers()
  const { loading, data, error } = useGetComponentGroupsQuery({
    variables: {
      uuid: shareIdentifier,
      type: type as ComponentFilterType,
      versionShortId,
    },
  })

  let sidebarContent = null

  if (loading) {
    sidebarContent = (
      <Flex flex="auto" width="100%" justifyContent="center" height="100%">
        <LoadingPlaceholder />
      </Flex>
    )
  }

  let componentsTree = null

  try {
    componentsTree = JSON.parse(
      data?.share?.version?.document?.componentGroups?.data ?? '[]'
    )
  } catch (e) {
    sidebarContent = <GroupsError />
  }

  if (componentsTree) {
    sidebarContent = (
      <SidebarList
        level={0}
        componentsTree={componentsTree}
        onSelect={onSelect}
        selected={selected}
      />
    )
  }

  if (error) {
    // log the error to sentry
    if (typeof error === 'string') {
      Sentry.captureException(error)
    } else if (error?.graphQLErrors) {
      const versionForbiddenError = error.graphQLErrors.some(e =>
        [
          'USER_CANT_VIEW_OLD_SHARE_VERSIONS',
          'USER_CANT_VIEW_VERSION_HISTORY',
          'USER_CANT_VIEW_SHARE_VERSION',
        ].includes(e.extensions?.reason)
      )

      if (!versionForbiddenError) {
        Sentry.captureException(error)
      }
    }

    sidebarContent = <GroupsError />
  }

  return (
    <Flex flex="auto">
      <Sidebar>{sidebarContent}</Sidebar>
      <Content>{children}</Content>
    </Flex>
  )
}
